export class KeyNotFoundError extends Error {
    name = "KeyNotFoundError";

    constructor(key: unknown, objName: string) {
        super(`Key ${JSON.stringify(key)} is not defined in ${objName}`);
    }
}

/** Get a property of an object if it exists, otherwise return `undefined`.
 *
 * Prefer using this instead of the `obj[key]` operator because it does not traverse the prototype chain.
 */
export function getOwn<T>(obj: T | null | undefined, prop: keyof T | string | null | undefined) {
    if (prop == null || obj == null) {
        return undefined;
    }

    return Object.hasOwn(obj, prop) ? obj[prop as keyof T] : undefined;
}

/** Check if an object has a property.
 *
 * Prefer using this instead of the `in` operator because it does not traverse the prototype chain.
 */
export function hasOwn<T, TKey extends keyof T | string>(
    obj: T | null | undefined,
    prop: TKey,
): obj is T & { [K in TKey]: unknown } {
    return obj != null && Object.hasOwn(obj, prop);
}

/** Get a property of an object if it exists, otherwise throw an error.
 *
 * @throws {KeyNotFoundError} if the property does not exist
 * @throws {TypeError} if the object is not defined
 */
export function mustGetProperty<T>(obj: T | null | undefined, prop: keyof T, objName?: string) {
    return mustGetOwn(obj as Record<string, unknown>, prop, objName) as T[keyof T];
}

/** @inheritDoc mustGetProperty */
export function mustGetOwn<T>(
    obj: Record<PropertyKey, T> | null | undefined,
    prop: PropertyKey,
    objName = "an object",
) {
    if (obj == null) {
        throw new TypeError(`${objName} is null or undefined`);
    }

    if (!Object.hasOwn(obj, prop)) {
        throw new KeyNotFoundError(prop, objName);
    }

    return obj[prop];
}
